import type { DraftLine, GridLine } from '../components/ReceiveDeliveryModal';
import type { ReceivableOrderOption } from '../goodsReceiptTypes';
import type { StorageFacilityOption } from '../inventoryTypes';

/**
 * Fills the Receive Delivery form so the primary action enables on the first try.
 *
 * <b>Every value is resolved against the LIVE feeds</b> — a real order, its real open lines, a
 * real store. An id the generator invented fails the server's "it must exist and be active"
 * check, which reads as a bug in the form rather than in the generator.
 *
 * <b>NOTHING HERE REACHES THE IDENTITY STORE</b>, so the values are plausible vocabulary rather
 * than the obviously-synthetic names `samplePersonnel` produces. A supplier is an organization
 * and every other field is a document number or a note. The test is where a value LANDS.
 */
export function sampleDelivery(
  orders: readonly ReceivableOrderOption[],
  lines: readonly GridLine[],
  facilities: readonly StorageFacilityOption[],
  current: readonly DraftLine[],
) {
  const today = new Date();
  const uid = Math.random().toString(36).slice(2, 8).toUpperCase();

  /*
    NEVER A FIELD THE READER HAS ALREADY DECIDED. An order already chosen stays chosen — picking
    a different one would discard the lines they have filled in, and a generator the reader
    cannot predict is worse than one that fills less.
  */
  const order = orders.length > 0 ? orders[0] : null;

  /*
    Rows are only produced once the lines have LOADED. Before that there is nothing to resolve
    against, and inventing a row would post an order line that is not on the order.
  */
  const byKey = new Map(lines.map((l) => [l.key, l]));

  const rows: DraftLine[] | null =
    current.length > 0
      ? current.map((row, index) => {
          const line = byKey.get(row.key);

          if (!line) return row;

          /*
            A PARTIAL DELIVERY ON THE FIRST LINE, in full on the rest. One short line is what
            makes the inspection gate worth exercising — a form that always delivered everything
            would never show a reader what a balance looks like.
          */
          /*
            A DONATION AND A TRANSFER HAVE NO OUTSTANDING QUANTITY to deliver against, so the
            generator picks a plausible one rather than a partial share of nothing. Ten is a
            delivery somebody could have made; null would leave the form unsubmittable, which
            is the one thing Autogenerate exists to prevent.
          */
          const available = line.openQuantity ?? 10;

          const quantity =
            index === 0 && available > 1 ? Math.max(1, Math.floor(available / 2)) : available;

          // Only a kind that SITS SOMEWHERE takes a store. A licence is kept nowhere, and a
          // store beside one is refused.
          const facility =
            line.sitsSomewhere && facilities.length > 0 ? facilities[0].id : null;

          /*
            ONE DAMAGED UNIT where the line carries enough to spare one, and nothing at all
            otherwise. The same reasoning as the short first line above: a generator that always
            delivered everything in perfect condition would never show a reader what the Good
            column does, and the breakdown is the half of this form most worth exercising.

            It stays within the quantity by construction, so the generated form can never open
            already refused — which is the one thing Autogenerate exists to prevent.
          */
          const damaged = quantity >= 2 ? 1 : 0;

          return {

            ...row,
            selected: true,
            quantityReceived: quantity,
            quantityExpired: 0,
            quantityDamaged: damaged,
            quantityWrongItem: 0,
            storageFacilityId: row.storageFacilityId ?? facility,

            // REQUIRED where the item is perishable, and refused where it is not.
            expiryDate: line.tracksExpiry
              ? row.expiryDate ?? iso(addMonths(today, 18))
              : null,

            unitCost: row.unitCost ?? line.unitCost,

            // Left blank so the server assigns `{ReceiptNumber}-Lnn`, which is the ordinary path.
            batchNumber: row.batchNumber,

            /*
              ONE PER UNIT on a serialized line, unique per click so a second press does not
              collide with the stock the first one created — AND per LINE, which this got wrong.

              It numbered every line from one sequence, so a delivery of two serialized lines
              produced SN-…-001 on both and the server accepted it: the duplicate check was
              per-line at the time. Both halves are fixed; the line index is what keeps this one
              honest.
            */
            serials: line.tracksSerial
              ? Array.from(
                  { length: quantity },
                  (_, i) => `SN-${uid}-${index + 1}-${String(i + 1).padStart(3, '0')}`,
                )
              : [],
            notes: row.notes,
          };
        })
      : null;

  return {
    order,
    rows,

    /**
     * A plausible giver. NOT a person — a donor is an ORGANIZATION on this form, so nothing
     * here reaches `rsys_pii` and the obviously-synthetic rule does not bind.
     */
    donorName: `Rotary Club of Antipolo ${uid}`,

    // A delivery is something that has happened, so never forward-dated.
    deliveredOn: iso(today),
    invoiceNumber: `SI-${uid}`,
    invoiceDate: iso(today),
    /*
      It read "Delivered complete and in good order", which the generator's own damaged unit
      made false on every row it filled. A note that contradicts the grid above it is worse
      than a bland one — the prose is part of the change.
    */
    notes: 'Checked against the supplier delivery note on arrival. See each line for condition.',
  };
}

function addMonths(from: Date, months: number): Date {
  const next = new Date(from);
  next.setMonth(next.getMonth() + months);
  return next;
}

/** A calendar date, read off the local parts — never through an instant conversion. */
function iso(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}
